/**
 * savedListings.ts — "My Saved Listings" for real.
 *
 * Card 1218241608398396. Ruling already made and worth restating because it
 * shapes this whole file: a save is a listing someone actually saved, NOT a
 * like. Different feature, no "Liked" section, and nothing here touches
 * listingLikes.ts's endpoints or its map.
 *
 * Built on listingLikes.ts's pattern — seed on cold load, a capture-phase
 * click listener, the write through a bridge, repaint — with one real
 * difference from it, and the difference is what makes this file shorter.
 *
 * ── SAVED ALREADY EXISTS, SO THERE IS NO SHADOW MAP TO KEEP IN SYNC ────────
 *
 * listingLikes.ts needed `__gbzLikedMap` because Blake's heart has no state
 * of its own — nothing in app.js remembers whether the viewer liked a
 * listing, so the fixup had to invent a place to keep it AND re-apply an `on`
 * class after every repaint (his bundle replaces #app wholesale). The save
 * tag is not that: `SAVED` is a real `Set` app.js already declares, and
 * renderCard already computes the tag's class straight off it —
 * `SAVED.has(l.id) && store.authed ? "on" : ""` (app.js:311, :1082) — and the
 * profile's "My Saved Listings" grid already reads `[...SAVED]` (app.js
 * ~1538-1543). So this file writes the real `SAVED` through the bridge and
 * calls his own repaint; there is no second class to apply and no
 * MutationObserver re-asserting anything, because the thing that would drift
 * IS the thing his renderer reads.
 *
 * ── THE ENDPOINTS, THE SAME THREE MOBILE USES ──────────────────────────────
 *
 *   GET  /api/favorites               -> [{ listing, seller }, …], newest first
 *   POST /api/favorites/:listingId    -> { saved }               (toggles)
 *   GET  /api/favorites/:listingId/status -> { saved }           (the re-read after a timeout)
 *
 * One endpoint toggles both directions — unlike likes' separate POST/DELETE,
 * there is no "which direction am I calling" branch. The client never even
 * needs to know the CURRENT state before calling: POST always flips it, and
 * the response says what it is now. `saved` is taken FROM THAT RESPONSE, not
 * computed locally — the whole point of this fixup.
 *
 * ── WHY SEED ALSO MERGES INTO LISTINGS, NOT JUST SAVED ─────────────────────
 *
 * `GET /api/favorites` answers full `{listing, seller}` rows (the same
 * envelope GET /api/listings uses — both run the row through
 * `toPublicListing`), and that is deliberate: a saved listing is not
 * guaranteed to be inside the boot-time `LISTINGS` snapshot (it holds only
 * the listings that were active when the page loaded), and the profile grid's
 * `[...SAVED].map((id) => LISTINGS.find(...))` renders nothing for an id
 * `LISTINGS` cannot resolve.
 * `__gbzMergeListing` (floorTransport.ts) is the SAME bridge
 * installRealDiscoverSearch and profileFixups.ts already use to add a row
 * `LISTINGS` does not yet have — first-write-wins, and a no-op for a listing
 * already on the floor. Filtered through `RENDERABLE_CATEGORY_SLUGS` first,
 * the same crash guard profileFixups.ts applies to its own bulk merge:
 * `renderDetail` dereferences `CATEGORIES.find(...)` with no fallback for a
 * category outside Blake's fixed nine.
 *
 * The seller half is a STUB, same shape as profileFixups.ts's — `GET
 * /api/favorites` selects only `{id, username, avatar, traderLevel}`, not
 * the full seller object `GET /api/listings` sends — but `__gbzMergeListing`
 * only writes `user` into `USERS` when that handle is absent, so a seller
 * already on the floor (the common case) keeps their real record; the stub
 * only ever backs a listing whose seller was not already known, and it is a
 * strictly BETTER stub than profileFixups.ts's all-null one since the real
 * avatar/traderLevel are already on hand.
 *
 * ── WHY THIS ONE STOPS PROPAGATION, AND WHY IT NEEDS NO WRITE-GUARD ENTRY ──
 *
 * Blake's own handler is bound directly on the tag (app.js:7603-7608,
 * `$$("[data-savelisting]").forEach((el) => el.addEventListener("click",
 * …))`): it flips `SAVED` locally, toggles `.on`, and toasts as if the save
 * were real. `stopPropagation()` here — document, CAPTURE phase, registered
 * before app.js ever runs — means that binding is never reached at all,
 * exactly the mechanism listingLikes.ts's own header explains at length.
 * Unlike the heart, the save tag IS already excluded from the card-open
 * handler (app.js:7641 lists `[data-savelisting]` among the eight), so the
 * stop is not what keeps a tap from navigating — it is what keeps Blake's
 * local toggle from running at all, which is the actual bug this file fixes.
 *
 * `[data-savelisting]` moves out of writeGuard.ts's `DELIBERATELY_UNGUARDED`
 * and into `GUARDED_WRITES` in the same change — see that file's own entry
 * for why it is a hand-added fail-safe rather than a mechanical derivation
 * (`SAVED` is not one of the roots `demoWriteGuardDerivation.test.ts` tracks,
 * the same reason `[data-follow]` had to be added by hand).
 *
 * ── A GUEST GETS THE SIGN-IN WALL, NOT A TOAST ────────────────────────────
 *
 * `installSavedListings` is installed unconditionally (WebInstall.tsx runs
 * the whole boot for a guest too, with `store.me` null). A guest's tap asks
 * guestWall.ts's `signedInOrWall` first, which opens Blake's own sign-in wall
 * and sends nothing. (This section used to say the opposite — a real 401 and
 * a `notify(SIGNIN_MESSAGE)` toast — which is the defect Blake's 10-01 staging
 * walk found.) `notify(SIGNIN_MESSAGE)` below remains for a session that
 * expired under a signed-in page.
 *
 * ── THE TOUR FIXTURE STAYS UNTOUCHED, AND WHY IT IS SAFE THAT WAY ─────────
 *
 * app.js's guided tour seeds a handful of demo ids straight into `SAVED`
 * (`tourFix.saves`, app.js ~4343-4413) so a guest walking the tour sees a
 * populated "My Saved Listings" section, and clears them on exit. Its own
 * comment says why: "the tour runs as a guest" — `GET /api/favorites`
 * requires auth, so for a guest this file's `seed()` does not even ask (WebInstall
 * passes `signedIn`; before that it 401'd) and returns without touching `SAVED`, the same fail-silent posture
 * listingLikes.ts's seed already uses. The two never run for the same
 * visitor in the same tab, so there is nothing to reconcile.
 *
 * ── SUCCESS COPY IS HIS, UNCHANGED, JUST FIRED FROM THE REAL RESULT ───────
 *
 * `SAVED_MESSAGE`/`REMOVED_MESSAGE` below are the exact strings his own
 * (now-suppressed) handler used to toast. Only the wiring was broken; the
 * words a visitor sees on a successful save were already right.
 */

import {
  mapListingRow,
  RENDERABLE_CATEGORY_SLUGS,
  type ApiListingRow,
  type DemoListing,
  type DemoUser,
} from "./floorTransport";
import { signedInOrWall } from "./guestWall";
import { awaitTap, createClock, isNoAnswer, readIsWayBack, settleOutranksRead, type Read } from "./instantTap";

export const SAVE_SELECTOR = "[data-savelisting]";

/** His own copy (app.js:7603-7608), preserved — see the file header's last section. */
export const SAVED_MESSAGE = "Saved · find it on your profile under My Saved Listings";
export const REMOVED_MESSAGE = "Removed from your saved listings";

export const SIGNIN_MESSAGE = "Sign in to save a listing";
/** Only for a failure the server gave no sentence for. */
export const GENERIC_FAILURE_MESSAGE = "Couldn't update that saved listing — please try again";
/** After the budget ran out and the status read could not say either way. */
export const UNCONFIRMED_MESSAGE = "Couldn't confirm that save. Check the tag before tapping again.";

/** `GET /api/favorites`'s row shape — `toPublicListing` on the listing half
 *  (identical to `ApiListingRow["listing"]`, both routes run the same
 *  serializer) and a PARTIAL seller (favorites.ts selects only these four
 *  columns; see the file header for why the gap is harmless). */
interface SavedListingRow {
  listing: ApiListingRow["listing"];
  seller: {
    id: string;
    username: string;
    avatar: string | null;
    traderLevel: string | null;
  };
}

interface SavedWindow extends Window {
  toast?: (message: string) => void;
  render?: () => void;
  rerenderKeepScroll?: () => void;
  __gbzSetListingSaved?: (id: string, saved: boolean) => void;
  __gbzListingSaved?: (id: string) => boolean;
  __gbzSeedSavedMap?: (ids: string[]) => number;
  __gbzMergeListing?: (listing: DemoListing, user: DemoUser) => boolean;
}

export function installSavedListings(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  {
    notify = (message: string) => {
      if (!win.document.getElementById("toast")) return;
      (win as SavedWindow).toast?.(message);
    },
    /* Whether anyone is signed in, as WebInstall's boot knows it (identity is fixed for the tab until a
       reload). A guest's /api/favorites only ever answered 401, so a guest's install skips the seed read
       (lane D's speed audit, win 4). Left out, the install reads, as it always did. */
    signedIn = true,
  }: { notify?: (m: string) => void; signedIn?: boolean } = {},
): () => void {
  /* In flight, per listing — the same re-entrancy guard listingLikes.ts uses.
     Blake's control had no such guard because it had no real request to
     race; with a real await, a double-tap is a toggle racing its own
     opposite. */
  const busy = new Set<string>();

  function repaint(): void {
    const w = win as SavedWindow;
    /* rerenderKeepScroll, not render — a save tag can sit halfway down the
       feed, and render() would jump the visitor back to the top of it. */
    if (typeof w.rerenderKeepScroll === "function") w.rerenderKeepScroll();
    else w.render?.();
    /* The SEED's repaint only (the grid needs the rows it merged). A tap never
       comes here: it paints its tag in place (paintTag, decision 4, card
       1219177901097382), and renderCard reads `SAVED` directly on his next
       paint, so there is no applyLikeState/MutationObserver equivalent. */
  }

  /* ── THE ORDER OF EVENTS (instantTap.ts header): one clock for this module. ── */
  const tick = createClock();
  /** The saved list as last read: what it said, when it was sent, when it landed. */
  let seed: Read<Set<string>> | undefined;
  /** Presses out right now, by listing: the saved state each one painted. Set at the paint,
      cleared at the settle or the put-back (pass 2, finding 1). */
  const pending = new Map<string, boolean>();
  /** The last settled state per listing (an answer or a status read), and when. */
  const settled = new Map<string, { saved: boolean; at: number }>();
  /** The latest press per listing. A late answer for an older press changes nothing. */
  const presses = new Map<string, number>();

  /**
   * Seed the viewer's saved set once, so a cold load's profile section
   * survives a reload instead of finding `SAVED` freshly cleared by
   * `installFloorListings`.
   *
   * Best-effort: a failure leaves `SAVED` empty, and a guest skips the read,
   * which is where it already was before this ran and where the tour's own
   * fixture expects to find it. Never a false positive.
   */
  async function seedSaves(): Promise<void> {
    if (!signedIn) return;
    const sentAt = tick();
    const res = await authFetch("/api/favorites");
    if (!res.ok) return;
    const rows = (await res.json().catch(() => null)) as SavedListingRow[] | null;
    if (!Array.isArray(rows)) return;

    const w = win as SavedWindow;
    const merge = w.__gbzMergeListing;
    const renderable = new Set(RENDERABLE_CATEGORY_SLUGS);
    const ids: string[] = [];
    for (const row of rows) {
      if (!row?.listing?.id) continue;
      ids.push(row.listing.id);
      if (typeof merge !== "function") continue;
      if (!renderable.has(row.listing.category)) continue; // same crash guard profileFixups.ts uses
      const { listing, user } = mapListingRow({
        listing: row.listing,
        seller: {
          id: row.seller.id,
          username: row.seller.username,
          displayName: null,
          avatar: row.seller.avatar,
          traderLevel: row.seller.traderLevel,
          tradeCount: null,
          createdAt: null,
        },
      });
      merge(listing, user);
    }
    seed = { value: new Set(ids), sentAt, landedAt: tick() };
    if (ids.length === 0) return; // nothing to seed, nothing worth a repaint (SAVED is untouched)
    w.__gbzSeedSavedMap?.(ids);
    /* The seed clears SAVED and refills it with the server's truth as of its send, so before its
       repaint: every press still painted is re-asserted (it must not take a tapped tag off while
       the answer is out), and so is every settle newer than the seed's send (a seed sent at boot
       and landing after a tap's answer is older than that answer). */
    for (const [id, s] of settled) if (settleOutranksRead(s.at, sentAt)) w.__gbzSetListingSaved?.(id, s.saved);
    for (const [id, want] of pending) w.__gbzSetListingSaved?.(id, want); // presses in flight LAST: intent outranks truth while out
    repaint();
  }

  /**
   * Every tag for this listing on screen, in place: renderCard's own class
   * (`SAVED.has(l.id) && store.authed ? "on" : ""`), applied by hand between his
   * paints. Decision 4 (2026-10-20): a tap paints the tag, never the view, so the
   * profile's My Saved Listings grid catches up on its next paint.
   */
  function paintTag(listingId: string, saved: boolean): void {
    for (const el of Array.from(win.document.querySelectorAll<HTMLElement>(SAVE_SELECTOR))) {
      if (el.dataset.savelisting === listingId) el.classList.toggle("on", saved);
    }
  }

  /** SAVED through the bridge and every tag on screen, from one state. */
  function show(listingId: string, saved: boolean): void {
    (win as SavedWindow).__gbzSetListingSaved?.(listingId, saved);
    paintTag(listingId, saved);
  }

  /** The press, settled: the state is the server's; the guess is no longer on screen. */
  function settle(listingId: string, saved: boolean): void {
    settled.set(listingId, { saved, at: tick() });
    pending.delete(listingId);
    show(listingId, saved);
  }

  /** The press, put back: the guess is no longer on screen. */
  function putBack(listingId: string, back: () => boolean): void {
    pending.delete(listingId);
    show(listingId, back());
  }

  /**
   * The server's answer, applied. Returns "unknown" when the toggle MAY have
   * been processed and only a read can say (a 502/504, an unreadable 2xx): the
   * caller re-reads then. `still`, for a LATE answer: whether this press is
   * still the latest, asked again after the body is read.
   */
  async function apply(listingId: string, want: boolean, res: Response, back: () => boolean, still: () => boolean = () => true): Promise<"settled" | "refused" | "unknown"> {
    if (res.status === 401) {
      putBack(listingId, back);
      notify(SIGNIN_MESSAGE);
      return "refused";
    }
    if (isNoAnswer(res.status)) {
      putBack(listingId, back);
      return "unknown";
    }
    if (!res.ok) {
      const b = back();
      putBack(listingId, back);
      const err = (await res.json().catch(() => ({}))) as { error?: string };
      if (!still()) return "refused";
      /* No words when the way back already shows what was asked (pass 4, finding 2). */
      if (b === want) return "refused";
      notify(err.error ?? GENERIC_FAILURE_MESSAGE);
      return "refused";
    }
    /* 🪤 THE STATE COMES FROM HERE, AND ONLY FROM HERE. POST toggles either
       direction and answers `{ saved }`. A 2xx with no boolean `saved` is a write
       that landed with an unreadable receipt: never guessed, read instead. */
    const body = (await res.json().catch(() => ({}))) as { saved?: boolean };
    if (!still()) return "settled";
    if (typeof body.saved !== "boolean") {
      putBack(listingId, back);
      return "unknown";
    }
    settle(listingId, body.saved);
    notify(body.saved ? SAVED_MESSAGE : REMOVED_MESSAGE);
    return "settled";
  }

  /**
   * After the budget ran out: the status route, read, says whether the toggle
   * landed. Never the toggle again (decision 1): a resent toggle would undo the
   * first one if it did land. `stale` is true once the original request
   * answered after all; checked AFTER the body is read, because a late answer
   * can land between this read's headers and its body.
   */
  async function reread(listingId: string, want: boolean, stale: () => boolean): Promise<void> {
    const outcome = await awaitTap(authFetch(`/api/favorites/${encodeURIComponent(listingId)}/status`));
    const body = outcome.kind === "answer" && outcome.res.ok
      ? ((await outcome.res.json().catch(() => ({}))) as { saved?: unknown })
      : {};
    if (stale()) return;
    if (typeof body.saved !== "boolean") {
      notify(UNCONFIRMED_MESSAGE);
      return;
    }
    settle(listingId, body.saved);
    notify(body.saved === want ? (body.saved ? SAVED_MESSAGE : REMOVED_MESSAGE) : GENERIC_FAILURE_MESSAGE);
  }

  async function toggle(listingId: string): Promise<void> {
    const w = win as SavedWindow;
    const before = w.__gbzListingSaved?.(listingId) ?? false;
    const want = !before;
    /* 🔴 PAINTED FROM THE TAP (card 1219177901097382): the tag and SAVED move
       now; the answer settles them and says the words; a refusal, a dropped
       request or a silent one puts them back. */
    show(listingId, want);
    const paintedAt = tick();
    pending.set(listingId, want);
    const press = (presses.get(listingId) ?? 0) + 1;
    presses.set(listingId, press);
    /* Busy holds the control for the whole press, so only a LATE answer (after the
       press is over) has to ask whether a newer press has taken the control. */
    const current = (): boolean => presses.get(listingId) === press;
    /* The way back: the seed's answer when the seed is the way back (it landed since
       the paint and was sent after this listing's last settle), else the tap-time
       state. A tap-time put-back over a landed seed left the tag off over a real
       save, and the retry it invited would have UNSAVED it; a seed sent at boot
       and landing after a later tap's answer is older than that answer. */
    const back = (): boolean => {
      const s = settled.get(listingId);
      /* This press's own status read settled while a late answer was still out: the
         newest truth, and a late refusal goes back to it (pass 3, finding 2). */
      if (s && s.at > paintedAt) return s.saved;
      if (readIsWayBack(seed, paintedAt, s?.at)) return seed!.value.has(listingId);
      return before;
    };

    const request = authFetch(`/api/favorites/${encodeURIComponent(listingId)}`, { method: "POST" });
    const outcome = await awaitTap(request);
    if (outcome.kind === "answer") {
      const verdict = await apply(listingId, want, outcome.res, back);
      /* A toggle whose answer was lost (502/504, an unreadable 2xx) may have landed: read. */
      if (verdict === "unknown") await reread(listingId, want, () => false);
      return;
    }
    putBack(listingId, back);
    if (outcome.kind === "thrown") {
      /* A dropped request can be a connection reset AFTER the write for a toggle:
         read rather than guess (a put-back alone invited a tap that unsaved). */
      await reread(listingId, want, () => false);
      return;
    }
    let answered = false;
    let readDone = false;
    /* A read after the press is over: it holds the tag busy like the first one (decision 3), so
       no newer press can start while it is out and nothing can make it stale. */
    const readAgain = async (): Promise<void> => {
      if (!current() || busy.has(listingId)) return;
      busy.add(listingId);
      try { await reread(listingId, want, () => false); } finally { busy.delete(listingId); }
    };
    void request.then(
      async (res) => {
        /* A late 502/504 says nothing about the write: the status read already out decides; if
           it has already finished, read once more (the write may have landed after it). */
        if (isNoAnswer(res.status)) { if (readDone) await readAgain(); return; }
        answered = true;
        if (!current()) return;
        const verdict = await apply(listingId, want, res, back, current);
        /* A late 2xx with an unreadable receipt: the write landed one way or the other, and the
           read that was out has stood down (`answered`): read again (pass 4, finding 1). */
        if (verdict === "unknown") await readAgain();
      },
      () => { /* failed after the budget: the status read is the truth */ },
    );
    await reread(listingId, want, () => answered);
    readDone = true;
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    const el = target.closest(SAVE_SELECTOR) as HTMLElement | null;
    if (!el) return;
    const listingId = el.dataset.savelisting;
    if (!listingId) return;

    /* See the file header's "WHY THIS ONE STOPS PROPAGATION" section. This
       is what keeps Blake's own local toggle (app.js:7603-7608) from running
       at all — not a navigation guard, the tag is already excluded from
       card-open (app.js:7641). */
    event.stopPropagation();
    event.preventDefault();

    // A guest gets the sign-in wall and no request — see guestWall.ts.
    if (!signedInOrWall(win)) return;
    if (busy.has(listingId)) return;
    busy.add(listingId);
    void toggle(listingId)
      .catch(() => notify(GENERIC_FAILURE_MESSAGE))
      .finally(() => busy.delete(listingId));
  };

  win.document.addEventListener("click", onClick, true);

  void seedSaves().catch(() => {
    /* A dropped seed is not an answer. `SAVED` reads empty, which is where
       it was before this file existed. */
  });

  return () => {
    win.document.removeEventListener("click", onClick, true);
  };
}
